// © 2026 AttainIP Engineering
/**
 * Enterprise-flavour builds (appConfig.accountType === 'enterprise') must
 * never render for a session whose real account isn't enterprise (RFC-0500 /
 * RFC-0121 A4) — e.g. a self-serve gmail magic-link account that ends up
 * authenticated inside /app/enterprise (the storage namespace is per-flavour,
 * so the enterprise app's own store can end up holding a self-serve session).
 *
 * Mounted once at the router root (wraps <Routes> in App.tsx) so it covers
 * every enterprise route — dashboard, upload, reports, etc. — not just the
 * org_admin-only Team section RequireOrgAdmin already guards.
 *
 * The redirect target is a different SPA build (the self-serve app), so this
 * is a hard navigation, not a React Router `<Navigate>`.
 */
import { useEffect, type ReactNode } from 'react';
import { appConfig } from '../../config/appConfig';
import { useAuthStore, isEnterpriseAccount } from '../../store/auth';

/** Self-service app root — stable across both the RFC-0500 Phase 1
 *  (`/app/enterprise`) and Phase 2 (`/enterprise`) routing layouts; the
 *  self-serve SPA stays at `/app` in either phase. */
const SELF_SERVE_APP_URL = '/app/';

interface EnterpriseAccountGuardProps {
  children: ReactNode;
}

export function EnterpriseAccountGuard({ children }: EnterpriseAccountGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);

  const blocked =
    appConfig.accountType === 'enterprise' && isAuthenticated && !!user && !isEnterpriseAccount(user);

  useEffect(() => {
    if (blocked) {
      window.location.href = SELF_SERVE_APP_URL;
    }
  }, [blocked]);

  if (blocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <p className="text-ds-text-secondary">
          This account isn't part of an enterprise firm. Redirecting you to the Attain app…
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
